/**
 * NOS-182: inert-form warning cards in the import review panel. Each form the
 * import detected on the source site renders as a warning ("this form isn't
 * connected — submissions go nowhere") with a one-click convert to the wired
 * NOS block (form / newsletter_signup → form-submissions inbox) and a dismiss
 * that keeps the imported markup as-is (some detections are third-party
 * widgets the org wants to keep). Never auto-converts.
 */
import { useCallback, useState } from "react"
import { Check, ClipboardX } from "lucide-react"
import type { SectionWithPage } from "@/hooks/useImportReviewSections"
import {
  convertDetectedForm,
  dismissDetectedForm,
  parseDetectedForm,
  type DetectedFormShape,
} from "@/lib/importedForms"

export interface DetectedFormsBlockProps {
  sections: SectionWithPage[]
  /** Called after a convert/dismiss writes changes (refreshes the builder). */
  onApplied?: () => void
}

interface FlaggedForm {
  sectionId: string
  pageTitle: string
  content: Record<string, unknown>
  detected: DetectedFormShape
}

type CardStatus = "idle" | "converting" | "dismissing" | "converted" | "dismissed" | "error"

/** "Name, Email, Message" — the detected fields, human-readable. */
export function fieldSummary(detected: DetectedFormShape): string {
  return detected.fields
    .map((f) => f.label ?? f.name.replace(/_/g, " "))
    .slice(0, 6)
    .join(", ")
}

export function flaggedFormsFromSections(sections: SectionWithPage[]): FlaggedForm[] {
  const out: FlaggedForm[] = []
  for (const { section, pageTitle, pageSlug } of sections) {
    const content = (section.content ?? {}) as Record<string, unknown>
    const detected = parseDetectedForm(content)
    if (!detected) continue
    out.push({
      sectionId: section.id,
      pageTitle: pageTitle ?? pageSlug,
      content,
      detected,
    })
  }
  return out
}

export function DetectedFormsBlock({ sections, onApplied }: DetectedFormsBlockProps) {
  const [status, setStatus] = useState<Record<string, CardStatus>>({})
  // Resolved cards stay visible with their outcome — the refresh after a
  // convert/dismiss un-flags the section, and a silently vanishing card reads
  // as a glitch instead of success (NOS-235 retry-label lesson).
  const [resolved, setResolved] = useState<Record<string, FlaggedForm>>({})

  const run = useCallback(
    async (form: FlaggedForm, action: "convert" | "dismiss") => {
      setStatus((prev) => ({
        ...prev,
        [form.sectionId]: action === "convert" ? "converting" : "dismissing",
      }))
      try {
        if (action === "convert") {
          await convertDetectedForm(form.sectionId, form.content, form.detected)
        } else {
          await dismissDetectedForm(form.sectionId, form.content)
        }
        setStatus((prev) => ({
          ...prev,
          [form.sectionId]: action === "convert" ? "converted" : "dismissed",
        }))
        setResolved((prev) => ({ ...prev, [form.sectionId]: form }))
        onApplied?.()
      } catch (err) {
        console.error("[DetectedFormsBlock] action failed:", action, err)
        setStatus((prev) => ({ ...prev, [form.sectionId]: "error" }))
      }
    },
    [onApplied]
  )

  const flagged = flaggedFormsFromSections(sections)
  const forms = [
    ...flagged.filter((f) => resolved[f.sectionId] === undefined),
    ...Object.values(resolved),
  ]
  if (forms.length === 0) return null

  const open = forms.filter((f) => {
    const s = status[f.sectionId]
    return s !== "converted" && s !== "dismissed"
  }).length

  return (
    <div style={BLOCK_STYLE} data-testid="detected-forms">
      <div style={BLOCK_TITLE_STYLE}>
        <ClipboardX size={12} style={{ flexShrink: 0 }} />
        {open > 0
          ? `${open} form${open !== 1 ? "s" : ""} from your old site ${open !== 1 ? "aren't" : "isn't"} connected`
          : "Imported forms reviewed"}
      </div>
      {open > 0 && (
        <div style={{ opacity: 0.75, marginBottom: 4 }}>
          Imported forms don&apos;t submit anywhere until you connect them — visitors&apos;
          messages would be lost.
        </div>
      )}
      {forms.map((form) => {
        const s = status[form.sectionId] ?? "idle"
        const isNewsletter = form.detected.intent === "newsletter"
        const busy = s === "converting" || s === "dismissing"
        return (
          <div key={form.sectionId} style={CARD_STYLE}>
            <div style={CARD_TITLE_STYLE}>
              {isNewsletter ? "Signup form" : "Contact form"} on {form.pageTitle}
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", marginBottom: 3 }}>
              Fields: {fieldSummary(form.detected)}
            </div>
            {form.detected.destination_email && (
              <div style={{ color: "rgba(255,255,255,0.55)", marginBottom: 3 }}>
                Your old site sent submissions to {form.detected.destination_email}. Converted
                forms deliver to your NOS inbox instead.
              </div>
            )}
            {s === "converted" ? (
              <div style={DONE_STYLE}>
                <Check size={11} style={{ flexShrink: 0 }} />
                Connected — submissions now land in your NOS inbox.
              </div>
            ) : s === "dismissed" ? (
              <div style={{ ...DONE_STYLE, color: "rgba(255,255,255,0.6)" }}>
                <Check size={11} style={{ flexShrink: 0 }} />
                Kept as imported.
              </div>
            ) : (
              <div>
                {s === "error" && (
                  <div style={ERROR_STYLE}>Couldn&apos;t apply that — try again.</div>
                )}
                <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
                  <button
                    type="button"
                    onClick={() => void run(form, "convert")}
                    disabled={busy}
                    style={{ ...CONVERT_BTN_STYLE, opacity: busy ? 0.6 : 1 }}
                  >
                    {s === "converting"
                      ? "Converting…"
                      : isNewsletter
                        ? "Convert to newsletter signup"
                        : "Convert to contact form"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void run(form, "dismiss")}
                    disabled={busy}
                    style={{ ...DISMISS_BTN_STYLE, opacity: busy ? 0.6 : 1 }}
                  >
                    {s === "dismissing" ? "…" : "Keep as is"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// === styles — amber warning tone (an inert form is a problem, not a feature) ===

const BLOCK_STYLE: React.CSSProperties = {
  margin: "0 14px 10px",
  padding: "8px 10px",
  background: "rgba(245,158,11,0.08)",
  border: "1px solid rgba(245,158,11,0.25)",
  borderRadius: 8,
  fontSize: 10,
  color: "rgba(255,255,255,0.75)",
}

const BLOCK_TITLE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontWeight: 600,
  fontSize: 11,
  color: "#f59e0b",
  marginBottom: 4,
}

const CARD_STYLE: React.CSSProperties = {
  padding: "7px 0 9px",
  borderTop: "1px solid rgba(245,158,11,0.18)",
}

const CARD_TITLE_STYLE: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 11,
  color: "rgba(255,255,255,0.9)",
  marginBottom: 3,
}

const CONVERT_BTN_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "6px 8px",
  background: "rgba(245,158,11,0.2)",
  border: "1px solid rgba(245,158,11,0.45)",
  borderRadius: 6,
  color: "#fbbf24",
  fontSize: 10.5,
  fontWeight: 600,
  cursor: "pointer",
}

const DISMISS_BTN_STYLE: React.CSSProperties = {
  padding: "6px 8px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  color: "rgba(255,255,255,0.75)",
  fontSize: 10.5,
  cursor: "pointer",
}

const DONE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginTop: 4,
  color: "#4ade80",
  fontWeight: 600,
  fontSize: 10.5,
}

const ERROR_STYLE: React.CSSProperties = {
  marginTop: 4,
  color: "#f87171",
}
